import { useEffect, useState } from "react";
import { FaCheckCircle, FaPaperPlane, FaSpinner } from "react-icons/fa";
import { defaultAxiosInstance } from "../api/defaultInstance";
import "../css/AdManagement.css";
import Layout from "./Layout";

function AdVerification() {
  const [isLoading, setIsLoading] = useState(false);
  const [ads, setAds] = useState([]);
  const [filter, setFilter] = useState({
    status: "전체",
    startDate: "",
    keyword: "",
    page: 1,
    limit: 50,
    advertisers: [],
    verified: 0,
  });
  const [selectedAds, setSelectedAds] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const advertisers = [
    "버즈빌",
    "호올스",
    "토스",
    "베이직",
    "길찾기",
    "당근",
    "멘토스",
    "브라보",
    "찰리",
    "제주",
    "투플",
    "기프트",
    "최적화",
    "핫팩",
    "카카오",
    "강남",
    "일키 트래픽",
    "썬데이",
    "백링크",
    "N4",
    "화이트",
  ];

  // 테이블 데이터만 조회하는 함수
  const fetchTableData = async (currentFilter = filter) => {
    const response = await defaultAxiosInstance.get(
      "/advertisement/admin/campaigns",
      {
        params: currentFilter,
      },
    );
    const result = response.data.data;
    setAds(result.campaigns || []);
    setTotalCount(result.totalCount || 0); // 🔥 여기 중요
  };

  // 초기 로딩 시 두 API 모두 호출
  useEffect(() => {
    fetchTableData();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;

    console.log("e.target: ", e.target);

    if (type === "checkbox") {
      // boolean 필터 (submitted, paid)
      if (name === "submitted" || name === "paid") {
        setFilter((prev) => ({ ...prev, [name]: checked ? true : null }));
      }
      // 복수 선택 (advertisers)
      else if (name === "advertisers") {
        setFilter((prev) => {
          const updated = checked
            ? [...prev.advertisers, value]
            : prev.advertisers.filter((v) => v !== value);
          return { ...prev, advertisers: updated };
        });
      }
    } else {
      // 일반 input
      setFilter((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 필터 적용 시 페이지를 1로 리셋하고, 두 API 호출
  const applyFilter = () => {
    const newFilter = { ...filter, page: 1 };
    setFilter(newFilter);
    fetchTableData(newFilter);
  };

  const resetFilter = () => {
    const initialFilter = {
      status: "전체",
      startDate: "",
      keyword: "",
      page: 1,
      limit: 50,
      advertisers: [],
      verified: 0,
    };
    setFilter(initialFilter);
    fetchTableData(initialFilter);
  };

  const handleCheckboxChange = (adId) => {
    setSelectedAds((prev) =>
      prev.includes(adId) ? prev.filter((id) => id !== adId) : [...prev, adId],
    );
  };

  // 헤더 체크박스: 전체 선택/해제 기능
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedAds(ads.map((ad) => ad.adCampaignId));
    } else {
      setSelectedAds([]);
    }
  };

  // 페이징: 이전 버튼 클릭 시 테이블 데이터만 조회
  const handlePrevPage = () => {
    const newPage = Math.max(1, filter.page - 1);
    const newFilter = { ...filter, page: newPage };
    setFilter(newFilter);
    fetchTableData(newFilter);
  };

  // 페이징: 다음 버튼 클릭 시 테이블 데이터만 조회
  const handleNextPage = () => {
    const newPage = filter.page + 1;
    const newFilter = { ...filter, page: newPage };
    setFilter(newFilter);
    fetchTableData(newFilter);
  };

  const handlePageClick = (page) => {
    const newFilter = { ...filter, page };
    setFilter(newFilter);
    fetchTableData(newFilter);
  };

  // 검수 완료 함수
  const handleVerifiedClick = async () => {
    if (selectedAds.length === 0) {
      alert("검수 완료 처리할 캠페인을 선택해주세요");
      return;
    }
    try {
      const response = await defaultAxiosInstance.post(
        "/advertisement/admin/campaigns/verified",
        {
          adCampaignIds: selectedAds,
        },
      );

      if (response.data.status === 200) {
        alert("검수 완료 처리되었습니다.");
        fetchTableData(); // 갱신
      }
    } catch (error) {
      console.error("결제 완료 처리 오류:", error);
      alert("검수 완료 처리 중 오류가 발생했습니다.");
    }
  };

  const handleVerifiedReqClick = async () => {
    setIsLoading(true); // 로딩 시작

    try {
      const response = await defaultAxiosInstance.post(
        "/advertisement/admin/campaigns/verifyKeywords",
      );

      if (response.data.status === 200) {
        alert("검수 요청이 완료되었습니다.");
        fetchTableData(); // 데이터 다시 불러오기
      }
    } catch (error) {
      console.error("검수 요청 처리 오류:", error);
      alert("검수 요청 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false); // 로딩 종료
    }
  };

  // 시작 인덱스와 끝 인덱스 계산
  const startIndex = (filter.page - 1) * filter.limit + 1;
  const endIndex = Math.min(filter.page * filter.limit, totalCount);

  return (
    <Layout>
      <div className="ad-management-container">
        <div className="filter-bar2">
          <div className="filter-group">
            <label>시행사</label>
            <div className="advertiser-select">
              {advertisers.map((adv) => (
                <label key={adv}>
                  <input
                    type="checkbox"
                    name="advertisers"
                    value={adv}
                    checked={filter.advertisers.includes(adv)}
                    onChange={handleFilterChange}
                  />{" "}
                  {adv}
                </label>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <label>검색</label>
            <input
              type="text"
              name="keyword"
              placeholder="플레이스명, 카테고리, 신청자"
              onChange={handleFilterChange}
              value={filter.keyword}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilter();
              }}
            />
          </div>
          <div className="filter-group">
            <label>상태</label>
            <select
              name="status"
              onChange={handleFilterChange}
              value={filter.status}
            >
              <option value="전체">전체</option>
              <option value="waiting">대기중</option>
              <option value="active">활성화</option>
              <option value="expiringSoon">종료임박</option>
              <option value="terminated">종료</option>
            </select>
          </div>
          <div className="filter-group">
            <label>시작일</label>
            <input
              type="date"
              name="startDate"
              onChange={handleFilterChange}
              value={filter.startDate}
            />
          </div>
          <div className="filter-group">
            <button onClick={applyFilter}>필터 적용</button>
            <button onClick={resetFilter} className="reset-btn">
              초기화
            </button>
          </div>
        </div>

        <div className="table-header">
          <h3 className="section-title">키워드 변경된 캠페인 목록</h3>

          <div
            className="button-group"
            style={{
              pointerEvents: isLoading ? "none" : "auto",
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <button
              className="status-btn request"
              onClick={handleVerifiedReqClick}
              disabled={isLoading}
            >
              {isLoading ? <FaSpinner className="spinner" /> : <FaPaperPlane />}
              {isLoading ? "요청 중..." : "검수요청"}
            </button>
            <button
              className="status-btn verified"
              onClick={handleVerifiedClick}
            >
              <FaCheckCircle /> 검수완료
            </button>
          </div>
        </div>

        <div className="ad-table">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      ads.length > 0 && selectedAds.length === ads.length
                    }
                  />
                </th>
                <th>번호</th>
                <th>신청자</th>
                <th>상품명</th>
                <th>플레이스명</th>
                <th>MID</th>
                <th>시작일</th>
                <th>종료일</th>
                <th>접수일</th>
                <th>키워드 변경</th>
                <th>타입 변경</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              {ads.map((ad, index) => (
                <tr key={ad.adCampaignId}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedAds.includes(ad.adCampaignId)}
                      onChange={() => handleCheckboxChange(ad.adCampaignId)}
                    />
                  </td>
                  <td>{index + 1}</td>
                  <td>{ad.userName}</td>
                  <td>
                    [{ad.advertiserName}]{ad.adName}
                  </td>
                  <td>{ad.placeName}</td>
                  <td>{ad.placeId}</td>
                  <td>{ad.startDate}</td>
                  <td>{ad.endDate}</td>
                  <td>{ad.submitDate}</td>
                  <td>
                    {ad.keywords.map((keyword, idx) => (
                      <div key={idx} className="change-item">
                        {keyword.prevKeyword && (
                          <>
                            <span className="prev-value">
                              {keyword.prevKeyword}
                            </span>
                            <span className="arrow">→</span>
                            <span className="new-value">
                              {keyword.adKeyword}
                            </span>
                          </>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    {ad.keywords.map((keyword, idx) => (
                      <div key={idx} className="change-item">
                        {keyword.previousType && (
                          <>
                            <span className="keyword-label">
                              {keyword.adKeyword}:
                            </span>
                            <span className="prev-value">
                              {keyword.previousType}
                            </span>
                            <span className="arrow">→</span>
                            <span className="new-value">{keyword.adType}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    <span className={`badge badge-${ad.status}`}>
                      {ad.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination-info">
          <div>
            총 {totalCount}개 항목 중 {startIndex} - {endIndex}
          </div>
          <div className="pagination">
            <button onClick={handlePrevPage} disabled={filter.page === 1}>
              이전
            </button>
            {/* 페이지 번호 버튼들 */}
            {Array.from(
              { length: Math.ceil(totalCount / filter.limit) },
              (_, i) => (
                <button
                  key={i + 1}
                  className={filter.page === i + 1 ? "active" : ""}
                  onClick={() => handlePageClick(i + 1)}
                >
                  {i + 1}
                </button>
              ),
            )}
            <button onClick={handleNextPage} disabled={endIndex >= totalCount}>
              다음
            </button>
          </div>
        </div>
      </div>
      <style>
        {`
                    .change-item {
                        margin-bottom: 8px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 14px;
                    }

                    .keyword-label {
                        color: #333;
                        font-weight: 500;
                        margin-right: 4px;
                    }

                    .prev-value {
                        color: #666;
                        text-decoration: line-through;
                    }

                    .arrow {
                        color: #999;
                        font-weight: bold;
                    }

                    .new-value {
                        color: #000;
                        font-weight: 500;
                    }

                    .change-item:last-child {
                        margin-bottom: 0;
                    }
                `}
      </style>
    </Layout>
  );
}

export default AdVerification;
